/**
 * The brand, and the one colour operation. Everything else a component
 * paints with comes from the Palette in force (palette.ts, read through
 * usePalette) -- the mode-independent constants live here because they
 * are the same in both modes.
 */
import type { Hex, Rgba } from './palette';

/** THE MARK'S OWN COLOUR, sampled from public/brand/polywog-mark.png
 * rather than eyeballed -- the logo is the source and everything else
 * matches it. The brand does not change with the mode. */
export const BRAND = {
  teal: '#00C0CD',
  /** 85% of the brand, for a pressed/hover state. */
  tealHover: '#00A3AE',
  /** Text on a teal fill. */
  onTeal: '#003035',
} as const satisfies Record<string, Hex>;

/** The weights type is set in, by role. */
export const WEIGHT = { regular: 400, medium: 500, strong: 600, heavy: 700 } as const;

/**
 * The ALPHAS a paint is washed at, by what the wash is for (a bare alpha beside a paint is a missing name). These are
 * opacities for `tint`; the palette's `*Wash` fields (`redWash`, `approvedWash`) are finished colours, never alphas.
 */
export const WASH_ALPHA = {
  /** The leaf being read's row, and the faint end of a ramp. */
  whisper: 0.06,
  /** A notice's ground and a drop target's. */
  fill: 0.1,
  /** An ink-coloured control while it is pressed. */
  inkPress: 0.12,
  /** A listbox row under the pointer or the focus. */
  menuHover: 0.14,
  /** A chip's or an outline control's resting wash. */
  rest: 0.15,
  /** The selected segment. */
  selected: 0.18,
  /** A washed control under the pointer. */
  hover: 0.24,
  /** The focus ring of a field. */
  focusRing: 0.25,
  /** The highlight on the words that differ between readers. */
  mark: 0.28,
  /** The edge of a flexed box. */
  flexedEdge: 0.3,
  /** A washed control while it is pressed. */
  press: 0.32,
  /** A notice's edge. */
  noticeEdge: 0.5,
  /** The strong end of a ramp. */
  rampEnd: 0.56,
  /** A status dot's glow. */
  glow: 0.6,
} as const;

/** The opacity a disabled control wears, and the trough of a live dot's pulse. */
export const DIM = { disabled: 0.45, pulse: 0.5 } as const;

/** A translucent wash of a paint (chip and button backgrounds, area
 * fills on the canvas). Palette values are hex, so the arithmetic is
 * exact and the result is a colour every consumer parses, a 2D
 * context included. */
export function tint(hex: Hex, alpha: number = WASH_ALPHA.rest): Rgba {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
